import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus } from 'lucide-react';
import { trackEvent, trackProps } from '../../helpers/utils/analytics';
import { isHandInPlay } from '../../helpers/utils/roundPhase';
import {
  canOfferShareTip,
  hasSharedBefore,
  recordShareTipShown,
  retireShareTip,
  SHARE_TIP_DELAY_MS,
} from '../../helpers/utils/shareTip';
import { useScreenLayout } from '../../helpers/utils/useScreenLayout';
import { GlobalContext } from '../../store/global-context';
import { Box } from '../ui/box';
import { Button } from '../ui/button';
import { HStack, VStack } from '../ui/stack';
import { Text } from '../ui/text';
import { ToastFrame } from '../ui/toastFrame';
import {
  TOAST_CLOSE_CLEARANCE,
  TOAST_INSET,
  TOAST_SURFACE,
  TOAST_TEXT_GAP,
  ToastCloseButton,
  ToastIconBadge,
} from '../ui/toastSurface';

const QUESTIONS = [
  '“What’s the score?”',
  '“Who’s dealing?”',
  '“What did I bid?”',
];
const REMINDER = ['Still getting asked “What’s the score?”'];

// The update notice's insets above its text, which also has an action row.
const TOP_INSET = '8px';
const TEXT_TOP_INSET = '6px';

/** Anything else on screen that's asking for the player's attention. */
const BUSY =
  '[role="dialog"], .update-toast, [data-scope="toast"][data-part="root"]';

interface ShareTipProps {
  onShare: () => void;
  onPrefetch?: () => void;
}

/**
 * The scorekeeper's prompt to share the game, part way into a hand after the
 * first: the cards are dealt and sorted, a few tricks are down, and everyone
 * has had a score to ask about. It sits over the bottom of the board, so any
 * tap elsewhere puts it away rather than getting in the way.
 */
const ShareTip = ({ onShare, onPrefetch }: ShareTipProps) => {
  const { role, roundHistory, currentRound } = useContext(GlobalContext);
  const wide = useScreenLayout() !== 'phone';
  const [show, setShow] = useState(false);
  const [showing, setShowing] = useState<1 | 2>(1);
  const tipRef = useRef<HTMLDivElement>(null);
  const armed =
    role === 'local' && roundHistory.length > 0 && isHandInPlay(currentRound);

  useEffect(() => {
    if (!armed || !canOfferShareTip()) return;
    const bidsInAt = Date.now();
    let timer: number | undefined;
    let sharedBefore = false;
    void hasSharedBefore().then((shared) => {
      sharedBefore = shared;
    });
    const stop = () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', offer);
    };
    function offer() {
      window.clearTimeout(timer);
      const left = SHARE_TIP_DELAY_MS - (Date.now() - bidsInAt);
      if (left > 0) {
        timer = window.setTimeout(offer, left);
        return;
      }
      // A locked phone: offer again when the scorekeeper comes back.
      if (document.hidden) return;
      stop();
      if (sharedBefore) {
        retireShareTip();
        return;
      }
      // Busy: the next hand asks again, with no try used up.
      if (document.querySelector(BUSY)) return;
      const nth = recordShareTipShown();
      trackEvent('share_tip_shown', { showing: nth });
      onPrefetch?.();
      setShowing(nth);
      setShow(true);
    }
    document.addEventListener('visibilitychange', offer);
    offer();
    return stop;
  }, [armed, onPrefetch]);

  useEffect(() => {
    if (!show) return;
    const putAway = (event: PointerEvent) => {
      if (tipRef.current?.contains(event.target as Node)) return;
      setShow(false);
      trackEvent('share_tip_dismissed', { method: 'outside', showing });
    };
    document.addEventListener('pointerdown', putAway, true);
    return () => document.removeEventListener('pointerdown', putAway, true);
  }, [show, showing]);

  // On body: the header's zoom and stacking would scale and trap it.
  return createPortal(
    <ToastFrame show={show} wide={wide} testId="share-tip">
      <Box
        ref={tipRef}
        role="status"
        {...TOAST_SURFACE}
        position="relative"
        zoom={wide ? 1.2 : 1}
        pl={TOAST_INSET}
        pr={TOAST_INSET}
        pt={TOP_INSET}
        pb={TOAST_INSET}
      >
        <ToastCloseButton
          {...trackProps('share_tip_dismissed', { method: 'close', showing })}
          onClick={() => setShow(false)}
        />
        <VStack gap="var(--spades-spacing-ms)" align="stretch">
          <HStack
            gap="var(--spades-spacing-ms)"
            align="flex-start"
            pt={TEXT_TOP_INSET}
          >
            <ToastIconBadge icon={<UserPlus size={18} />} />
            <VStack gap={TOAST_TEXT_GAP} align="start" flex="1" minW="1px">
              {(showing === 2 ? REMINDER : QUESTIONS).map((line) => (
                <Text
                  key={line}
                  pr={TOAST_CLOSE_CLEARANCE}
                  fontWeight="700"
                  fontSize="var(--spades-font-size-sm)"
                  lineHeight={1.25}
                >
                  {line}
                </Text>
              ))}
              <Text
                // Three questions carry it past the X; the reminder's one line
                // doesn't.
                pr={showing === 2 ? TOAST_CLOSE_CLEARANCE : undefined}
                fontSize="var(--spades-font-size-xs)"
                fontWeight="400"
                lineHeight={1.25}
                color="var(--spades-muted)"
              >
                Anyone can see the game from their own perspective at the table.
              </Text>
            </VStack>
          </HStack>
          <Button
            variant="primary"
            h="44px"
            minH="44px"
            w="100%"
            {...trackProps('share_tip_opened', { showing })}
            onClick={() => {
              retireShareTip();
              setShow(false);
              onShare();
            }}
          >
            Invite Players to This Game
          </Button>
        </VStack>
      </Box>
    </ToastFrame>,
    document.body,
  );
};

export default ShareTip;
